



import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Save, AlertCircle, X } from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../../../utility/apiInstance";
import { TabFlightInfoOceanProps, VesselDetail } from "./types/MilestoneType";
import { getMinDateTime } from "./utils";
import { useRole } from "../../MangeContextProvide";
import { ContainerOffline } from "./ContainerOffline";

// ── Types ─────────────────────────────────────────────────────────────────────

interface PortOption {
  label: string;
  value: string;
  item: any;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  root: { padding: "14px 0 0" },

  // section header
  sectionHeader: {
    display: "flex", alignItems: "center",
    justifyContent: "space-between", marginBottom: "12px",
  },
  sectionTitle: { display: "flex", alignItems: "center", gap: "7px" },
  sectionAccent: { width: "3px", height: "16px", background: "#F8B71F", borderRadius: "99px" },
  sectionLabel: {
    fontSize: "12px", fontWeight: 600, color: "#374151",
    textTransform: "uppercase" as const, letterSpacing: "0.06em",
  },
  rowCount: { fontSize: "11px", color: "#9ca3af", fontWeight: 500 },

  // column headers
  colHeaders: {
    display: "grid",
    gridTemplateColumns: "90px 120px 110px 120px 100px 120px 148px 120px 80px 40px",
    padding: "6px 6px",
    marginBottom: "6px",
    minWidth: "1100px",
  },
  colHeader: {
    fontSize: "10.5px", fontWeight: 600, color: "#9ca3af",
    textTransform: "uppercase" as const, letterSpacing: "0.07em", padding: "0 5px",
  },

  // row
  rowWrap: { position: "relative" as const, marginBottom: "6px" },
  rowCard: {
    display: "grid",
    gridTemplateColumns: "90px 120px 110px 120px 100px 120px 148px 120px 80px 40px",
    background: "#fafafa",
    border: "1px solid #ececec",
    borderRadius: "10px",
    padding: "6px 6px",
    alignItems: "start",
    transition: "border-color 0.15s, box-shadow 0.15s",
    position: "relative" as const,
    minWidth: "1100px",
  },
  rowCardSaved: { background: "#fff", borderColor: "#efefef" },
  rowCardNew: { borderLeft: "3px solid #F8B71F" },
  rowCardError: { borderColor: "#fca5a5", background: "#fffafa" },
  rowCardHovered: { borderColor: "#e0e0e0", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" },
  cell: { padding: "0 5px" },

  // carrier badge
  carrierBadge: {
    display: "inline-flex", alignItems: "center", gap: "6px",
    background: "#1a1a1a", color: "#fff",
    fontSize: "12px", fontWeight: 600,
    padding: "4px 10px", borderRadius: "6px",
    whiteSpace: "nowrap" as const,
  },
  carrierDot: {
    width: "6px", height: "6px",
    borderRadius: "50%", background: "#F8B71F", flexShrink: 0,
  },

  // read-only values
  val: { fontSize: "12px", color: "#374151", padding: "6px 0", lineHeight: 1.4, fontWeight: 500 },
  valSub: { fontSize: "10.5px", color: "#9ca3af", marginTop: "1px" },
  valNa: { fontSize: "12px", color: "#9ca3af", padding: "6px 0" },

  // inputs
  input: {
    border: "1px solid #e2e2e2", borderRadius: "6px",
    padding: "6px 8px", fontSize: "11.5px", fontFamily: "inherit",
    color: "#374151", background: "#fff", outline: "none", width: "100%",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  inputDisabled: { background: "#f9f9f9", color: "#9ca3af", cursor: "not-allowed" },
  dateInput: {
    border: "1px solid #e2e2e2", borderRadius: "6px",
    padding: "6px 8px", fontSize: "11px", fontFamily: "inherit",
    color: "#374151", background: "#fff", outline: "none",
    width: "100%", cursor: "pointer",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  inputError: { borderColor: "#ef4444" },

  // error
  errText: {
    color: "#ef4444", fontSize: "10.5px",
    marginTop: "3px", display: "flex", alignItems: "center", gap: "3px",
  },

  // floating actions
  rowActions: {
    display: "flex", flexDirection: "row" as const, gap: "4px",
  },
  rowActionBtn: {
    width: "28px", height: "28px", borderRadius: "7px",
    border: "none", display: "flex", alignItems: "center",
    justifyContent: "center", cursor: "pointer",
    transition: "background 0.15s",
  },

  // add row
  addRowBtn: {
    background: "none", border: "1px dashed #d1d5db", borderRadius: "8px",
    padding: "6px 14px", fontSize: "12px", color: "#9ca3af",
    fontFamily: "inherit", cursor: "pointer", width: "100%", marginTop: "4px",
    transition: "border-color 0.15s, color 0.15s",
  },

  // divider
  divider: { border: "none", borderTop: "1px solid #f0f0f0", margin: "20px 0" },

  // footer
  footer: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "12px 0 0", borderTop: "1px solid #f0f0f0", marginTop: "8px",
  },
  footerLeft: { fontSize: "11.5px", color: "#9ca3af" },
  saveBtn: {
    display: "inline-flex", alignItems: "center", gap: "6px",
    background: "#F8B71F", border: "none", borderRadius: "7px",
    padding: "7px 16px", fontSize: "12.5px", fontWeight: 600,
    color: "#1a1a1a", cursor: "pointer", fontFamily: "inherit",
    boxShadow: "0 1px 4px rgba(248,183,31,0.35)", transition: "background 0.15s",
  },
  saveBtnDisabled: { background: "#e5e7eb", color: "#9ca3af", cursor: "not-allowed", boxShadow: "none" },

  // container grid
  containerColHeaders: {
    display: "grid", gridTemplateColumns: "1fr 1fr 60px",
    padding: "0 12px", marginBottom: "6px",
  },
  containerRowCard: {
    display: "grid", gridTemplateColumns: "1fr 1fr 60px",
    background: "#fafafa", border: "1px solid #ececec",
    borderRadius: "10px", padding: "10px 12px",
    alignItems: "center", marginBottom: "6px",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
};

// ── react-select styles ───────────────────────────────────────────────────────

const selectStyles = (hasError = false) => ({
  control: (base: any, state: any) => ({
    ...base,
    fontSize: "11.5px",
    minHeight: "30px",
    borderColor: hasError ? "#ef4444" : state.isFocused ? "#F8B71F" : "#e2e2e2",
    boxShadow: state.isFocused
      ? hasError ? "0 0 0 2px rgba(239,68,68,0.18)" : "0 0 0 2px rgba(248,183,31,0.18)"
      : "none",
    "&:hover": { borderColor: hasError ? "#ef4444" : "#F8B71F" },
    borderRadius: "6px",
    background: "#fff",
  }),
  option: (base: any, state: any) => ({
    ...base, fontSize: "11.5px",
    background: state.isSelected ? "#F8B71F" : state.isFocused ? "#fef9c3" : "#fff",
    color: state.isSelected ? "#1a1a1a" : "#374151",
  }),
  placeholder: (base: any) => ({ ...base, fontSize: "11.5px", color: "#9ca3af" }),
  menu: (base: any) => ({ ...base, borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 20 }),
  valueContainer: (base: any) => ({ ...base, padding: "2px 6px" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base: any) => ({ ...base, padding: "3px" }),
});

// ── Sub-components ────────────────────────────────────────────────────────────

function ErrorHint({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div style={s.errText}>
      <AlertCircle size={10} />
      {message}
    </div>
  );
}

function SectionHeader({ label, meta }: { label: string; meta?: string }) {
  return (
    <div style={s.sectionHeader}>
      <div style={s.sectionTitle}>
        <div style={s.sectionAccent} />
        <span style={s.sectionLabel}>{label}</span>
      </div>
      {meta && <span style={s.rowCount}>{meta}</span>}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function TabFlightInfoOcean({
  vesselsFormData,
  setVesselsFormData,
  awbNumber,
  isOnline,
}: TabFlightInfoOceanProps) {
  const { trackingData, setTrackingData } = useRole() as any;

  const [requiredData, setRequiredData] = useState<{
    carriers: { label: string; value: string }[];
    ports: PortOption[];
  }>({ carriers: [], ports: [] });

  const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
  const [filteredPorts, setFilteredPorts] = useState<PortOption[]>([]);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [containerData, setContainerData] = useState<any[]>([{ container_number: "", type: "" }]);

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const getCarriers = async () => {
    try {
      const { data } = await axiosInstance.get(`api/v1/ocean/shippingline`);
      setRequiredData(prev => ({
        ...prev,
        carriers: (data?.data || []).map((el: any) => ({ label: el?.scacCode || "", value: el.scacCode })),
      }));
    } catch { }
  };

  const getPorts = async () => {
    try {
      const { data } = await axiosInstance.get(`/api/v1/port/ocean`);
      const mapped: PortOption[] = (data?.data || []).map((el: any) => ({
        label: el?.port_name || "", value: el.port_name, item: el,
      }));
      setRequiredData(prev => ({ ...prev, ports: mapped }));
      setFilteredPorts(mapped.slice(1, 50));
    } catch { }
  };

  const fetchVessels = async () => {
    if (!awbNumber) return;
    try {
      const res = await axiosInstance.get(`/api/v1/offline-vessel/${awbNumber}`);
      const data: VesselDetail[] = res.data.data || [];
      if (data.length > 0) {
        setVesselsFormData(data.map(v => ({
          _id: v._id || Date.now().toString(),
          oceanTrackingNumber: v.oceanTrackingNumber,
          vesselSequence: v.vesselSequence || 0,
          name: v.name || "",
          voyage: v.voyage || "",
          etd: v.etd || "",
          eta: v.eta || "",
          placeOfReceipt: v.placeOfReceipt || "",
          placeOfReceiptCode: v.placeOfReceiptCode || "",
          placeOfReceiptCountry: v.placeOfReceiptCountry || "",
          origin: v.origin || "",
          originCode: v.originCode || "",
          originCountry: v.originCountry || "",
          destination: v.destination || "",
          destinationCode: v.destinationCode || "",
          destinationCountry: v.destinationCountry || "",
          placeOfDelivery: v.placeOfDelivery || "",
          placeOfDeliveryCode: v.placeOfDeliveryCode || "",
          placeOfDeliveryCountry: v.placeOfDeliveryCountry || "",
          carrier: awbNumber.split("-")[1] || "",
          isNew: false,
        })));
      } else {
        setVesselsFormData([{
          _id: Date.now().toString(),
          oceanTrackingNumber: awbNumber, vesselSequence: 0,
          name: "", voyage: "", etd: "", eta: "",
          placeOfReceipt: "", placeOfReceiptCode: "", placeOfReceiptCountry: "",
          origin: "", originCode: "", originCountry: "",
          destination: "", destinationCode: "", destinationCountry: "",
          placeOfDelivery: "", placeOfDeliveryCode: "", placeOfDeliveryCountry: "",
          carrier: awbNumber.split("-")[1] || "",
          isNew: true,
        }]);
      }
    } catch { }
  };

  const fetchTrackingData = async () => {
    try {
      const { data } = await axiosInstance.post("/api/v1/tracking/data", { type: "ocean", trackingNumber: awbNumber });
      setTrackingData(data.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.response?.data?.error || "Something went wrong!");
    }
  };

  const getListOfflineContainer = async () => {
    try {
      const res = await axiosInstance.get(`api/v1/ocean/offilne-container/${awbNumber}`);
      if (res?.data?.data?.length > 0) setContainerData(res.data.data);
    } catch { }
  };

  useEffect(() => { getCarriers(); getPorts(); }, []);
  useEffect(() => {
    fetchVessels();
    if (!isOnline) getListOfflineContainer();
  }, [awbNumber, setVesselsFormData]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleSearch = (input: string) => {
    const q = input.toLowerCase();
    setFilteredPorts(
      q
        ? requiredData.ports.filter(p => p.label.toLowerCase().includes(q)).slice(0, 50)
        : requiredData.ports.slice(0, 50)
    );
  };

  const handleChange = (id: string, data: Partial<VesselDetail>) => {
    setVesselsFormData(prev => prev.map(v => v._id === id ? { ...v, ...data } : v));
    setErrors(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        ...Object.keys(data).reduce((acc, k) => { acc[k] = ""; return acc; }, {} as Record<string, string>),
        ...("etd" in data || "eta" in data ? { eta: "" } : {}),
      },
    }));
  };

  const handleAdd = () => {
    setVesselsFormData(prev => [...prev, {
      _id: Date.now().toString(), oceanTrackingNumber: awbNumber,
      vesselSequence: prev.length + 1,
      name: "", voyage: "", etd: "", eta: "",
      origin: "", originCode: "", originCountry: "",
      destination: "", destinationCode: "", destinationCountry: "",
      portOfDischarge: "", portOfDischargeCode: "", portOfDischargeCountry: "",
      placeOfReceipt: "", placeOfReceiptCode: "", placeOfReceiptCountry: "",
      placeOfDelivery: "", placeOfDeliveryCode: "", placeOfDeliveryCountry: "",
      carrier: awbNumber.split("-")[1] || "",
      isNew: true,
    }]);
  };

  const handleDelete = async (id: string) => {
    const v = vesselsFormData.find(v => v._id === id);
    if (v && !v.isNew) {
      try {
        await axiosInstance.post(`/api/v1/offline-vessel/delete/${v._id}`, {
          oceanTrackingNumber: v.oceanTrackingNumber,
        });
      } catch {
        toast.error("Failed to delete vessel!"); return;
      } finally {
        fetchTrackingData();
      }
    }
    setVesselsFormData(prev => prev.filter(v => v._id !== id));
  };

  const validate = (): boolean => {
    let hasError = false;
    const newErrors: Record<string, Record<string, string>> = {};
    vesselsFormData.forEach(v => {
      const e: Record<string, string> = {};
      if (!v.carrier) e.carrier = "Carrier is required";
      if (!v.origin) e.origin = "Origin is required";
      if (!v.destination) e.destination = "Destination is required";
      if (v.origin && v.destination && v.origin === v.destination)
        e.destination = "Destination must differ from Origin";
      if (!v.etd) e.etd = "ETD is required";
      if (!v.eta) e.eta = "ETA is required";
      if (v.etd && v.eta && new Date(v.eta) <= new Date(v.etd))
        e.eta = "ETA must be after ETD";
      if (!v.name) e.name = "Vessel name is required";
      if (!v.voyage) e.voyage = "Voyage is required";
      if (Object.keys(e).length > 0) { newErrors[v._id!] = e; hasError = true; }
    });
    setErrors(newErrors);
    return !hasError;
  };

  const handleSave = async () => {
    if (!validate()) { toast.error("Please fill all required fields."); return; }
    try {
      await Promise.all(
        vesselsFormData
          .filter(v => v.isNew)
          .map((v, i) => axiosInstance.post(`/api/v1/offline-vessel/create`, {
            oceanTrackingNumber: v.oceanTrackingNumber,
            vesselSequence: i + 1,
            name: v.name,
            voyage: v.voyage,
            etd: v.etd,
            eta: v.eta,
            carrier: v.carrier,
            placeOfReceipt: v.placeOfReceipt || "",
            placeOfReceiptCode: v.placeOfReceiptCode || "",
            placeOfReceiptCountry: v.placeOfReceiptCountry || "",
            origin: v.origin,
            originCode: v.originCode || "",
            originCountry: v.originCountry || "",
            destination: v.destination,
            destinationCode: v.destinationCode || "",
            destinationCountry: v.destinationCountry || "",
            placeOfDelivery: v.placeOfDelivery || "",
            placeOfDeliveryCode: v.placeOfDeliveryCode || "",
            placeOfDeliveryCountry: v.placeOfDeliveryCountry || "",
          }))
      );
      toast.success("Vessels saved successfully!");
      fetchVessels();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save vessels!");
    } finally {
      fetchTrackingData();
    }
  };

  const newCount = vesselsFormData.filter(v => v.isNew).length;
  const isSaveDisabled = newCount === 0;

  // ── Render ───────────────────────────────────────────────────────────────────

  const VESSEL_COLS = ["Carrier", "Place of Receipt", "Port of Origin", "Port of Discharge", "Place of Delivery", "ETD", "ETA", "Vessel Name", "Voyage",""];

  return (
    <div style={s.root}>

      {/* Vessel section header */}
      <SectionHeader
        label="Vessel Info"
        meta={`${vesselsFormData.length} vessel${vesselsFormData.length !== 1 ? "s" : ""}${newCount > 0 ? ` · ${newCount} pending` : ""}`}
      />

      {/* <div style={{ overflowX: "auto" }}> */}
      <div>
        {/* Column headers */}
        <div style={s.colHeaders}>
          {VESSEL_COLS.map(h => <span key={h} style={s.colHeader}>{h}</span>)}
        </div>

        {/* Vessel rows */}
        {vesselsFormData.map((vessel, index) => {
          const isHovered = hoveredRow === vessel._id;
          const hasError = !!errors[vessel._id!] && Object.values(errors[vessel._id!]).some(Boolean);
          const rowStyle: React.CSSProperties = {
            ...s.rowCard,
            ...(vessel.isNew ? s.rowCardNew : s.rowCardSaved),
            ...(hasError ? s.rowCardError : {}),
            ...(isHovered ? s.rowCardHovered : {}),
          };

          return (
            <div
              key={vessel._id}
              style={s.rowWrap}
              onMouseEnter={() => setHoveredRow(vessel._id ?? null)}
              onMouseLeave={() => setHoveredRow(null)}
            >
              <div style={rowStyle}>

                {/* Carrier — always read-only badge */}
                <div style={s.cell}>
                  <div style={s.carrierBadge}>
                    <span style={s.carrierDot} />
                    {vessel.carrier || "—"}
                  </div>
                </div>

                {/* Place of Receipt */}
                <div style={s.cell}>
                  {isOnline || !vessel.isNew ? (
                    <div style={vessel.placeOfReceipt ? s.val : s.valNa}>{vessel.placeOfReceipt || "N/A"}</div>
                  ) : (
                    <>
                      <Select
                        styles={selectStyles(!!errors[vessel._id!]?.placeOfReceipt)}
                        options={filteredPorts}
                        onInputChange={handleSearch}
                        placeholder="Place of Receipt"
                        value={requiredData.ports.find(o => o.value === vessel.placeOfReceipt) || null}
                        onChange={sel => handleChange(vessel._id!, {
                          placeOfReceipt: sel?.value || "",
                          placeOfReceiptCode: sel?.item?.port_code || "",
                          placeOfReceiptCountry: sel?.item?.country_code || "",
                        })}
                      />
                      <ErrorHint message={errors[vessel._id!]?.placeOfReceipt} />
                    </>
                  )}
                </div>

                {/* Port of Origin */}
                <div style={s.cell}>
                  {isOnline|| !vessel.isNew ? (
                    <>
                      <div style={vessel.origin ? s.val : s.valNa}>{vessel.origin || "N/A"}</div>
                      {vessel.originCode && <div style={s.valSub}>{vessel.originCode}</div>}
                    </>
                  ) : (
                    <>
                      <Select
                        styles={selectStyles(!!errors[vessel._id!]?.origin)}
                        options={filteredPorts}
                        onInputChange={handleSearch}
                        placeholder="Port of Origin"
                        value={requiredData.ports.find(o => o.value === vessel.origin) || null}
                        onChange={sel => handleChange(vessel._id!, {
                          origin: sel?.value || "",
                          originCode: sel?.item?.port_code || "",
                          originCountry: sel?.item?.country_code || "",
                        })}
                      />
                      <ErrorHint message={errors[vessel._id!]?.origin} />
                    </>
                  )}
                </div>

                {/* Port of Discharge */}
                <div style={s.cell}>
                  {isOnline|| !vessel.isNew ? (
                    <>
                      <div style={vessel.destination ? s.val : s.valNa}>{vessel.destination || "N/A"}</div>
                      {vessel.destinationCode && <div style={s.valSub}>{vessel.destinationCode}</div>}
                    </>
                  ) : (
                    <>
                      <Select
                        styles={selectStyles(!!errors[vessel._id!]?.destination)}
                        options={filteredPorts}
                        onInputChange={handleSearch}
                        placeholder="Port of Discharge"
                        value={requiredData.ports.find(o => o.value === vessel.destination) || null}
                        onChange={sel => handleChange(vessel._id!, {
                          destination: sel?.value || "",
                          destinationCode: sel?.item?.port_code || "",
                          destinationCountry: sel?.item?.country_code || "",
                        })}
                      />
                      <ErrorHint message={errors[vessel._id!]?.destination} />
                    </>
                  )}
                </div>

                {/* Place of Delivery */}
                <div style={s.cell}>
                  {isOnline|| !vessel.isNew ? (
                    <div style={vessel.placeOfDelivery ? s.val : s.valNa}>{vessel.placeOfDelivery || "N/A"}</div>
                  ) : (
                    <>
                      <Select
                        styles={selectStyles(!!errors[vessel._id!]?.placeOfDelivery)}
                        options={filteredPorts}
                        onInputChange={handleSearch}
                        placeholder="Place of Delivery"
                        value={requiredData.ports.find(o => o.value === vessel.placeOfDelivery) || null}
                        onChange={sel => handleChange(vessel._id!, {
                          placeOfDelivery: sel?.value || "",
                          placeOfDeliveryCode: sel?.item?.port_code || "",
                          placeOfDeliveryCountry: sel?.item?.country_code || "",
                        })}
                      />
                      <ErrorHint message={errors[vessel._id!]?.placeOfDelivery} />
                    </>
                  )}
                </div>

                {/* ETD */}
                <div style={s.cell}>
                  {isOnline || !vessel.isNew ? (
                    <div style={vessel.etd ? s.val : s.valNa}>{vessel.etd || "N/A"}</div>
                  ) : (
                    <>
                      <input
                        type="datetime-local"
                        style={{ ...s.dateInput, ...(errors[vessel._id!]?.etd ? s.inputError : {}) }}
                        min={getMinDateTime()}
                        value={vessel.etd || ""}
                        onMouseDown={(e: any) => { e.preventDefault(); e.target.showPicker(); }}
                        onChange={e => handleChange(vessel._id!, { etd: e.target.value })}
                      />
                      <ErrorHint message={errors[vessel._id!]?.etd} />
                    </>
                  )}
                </div>

                {/* ETA */}
                <div style={s.cell}>
                  {isOnline || !vessel.isNew ? (
                    <div style={vessel.eta ? s.val : s.valNa}>{vessel.eta || "N/A"}</div>
                  ) : (
                    <>
                      <input
                        type="datetime-local"
                        style={{ ...s.dateInput, ...(errors[vessel._id!]?.eta ? s.inputError : {}) }}
                        min={vessel.etd || getMinDateTime()}
                        value={vessel.eta || ""}
                        onMouseDown={(e: any) => { e.preventDefault(); e.target.showPicker(); }}
                        onChange={e => handleChange(vessel._id!, { eta: e.target.value })}
                      />
                      <ErrorHint message={errors[vessel._id!]?.eta} />
                    </>
                  )}
                </div>

                {/* Vessel Name */}
                <div style={s.cell}>
                  <input
                    type="text"
                    style={{
                      ...s.input,
                      ...(isOnline ? s.inputDisabled : {}),
                      ...(errors[vessel._id!]?.name ? s.inputError : {}),
                    }}
                    placeholder="VESSEL NAME"
                    value={vessel.name}
                    disabled={isOnline}
                    onChange={e => handleChange(vessel._id!, { name: e.target.value.toUpperCase() })}
                  />
                  <ErrorHint message={errors[vessel._id!]?.name} />
                </div>

                {/* Voyage */}
                <div style={s.cell}>
                  <input
                    type="text"
                    style={{
                      ...s.input,
                      ...(isOnline ? s.inputDisabled : {}),
                      ...(errors[vessel._id!]?.voyage ? s.inputError : {}),
                    }}
                    placeholder="Voyage"
                    value={vessel.voyage}
                    disabled={isOnline}
                    onChange={e => handleChange(vessel._id!, { voyage: e.target.value.toUpperCase() })}
                  />
                  <ErrorHint message={errors[vessel._id!]?.voyage} />
                </div>
                {/* Floating row actions */}
                {!isOnline && (
                  <div style={s.rowActions}>
                    <button
                      style={{ ...s.rowActionBtn, background: "#fee2e2", color: "#dc2626" }}
                      onClick={() => handleDelete(vessel._id!)}
                      title="Remove vessel"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>


            </div>
          );
        })}

      </div>{/* /overflow-x */}

      {/* Add vessel */}
      {!isOnline && (
        <button
          style={s.addRowBtn}
          onClick={handleAdd}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#F8B71F"; (e.currentTarget as HTMLButtonElement).style.color = "#374151"; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#d1d5db"; (e.currentTarget as HTMLButtonElement).style.color = "#9ca3af"; }}
        >
          + Add vessel leg
        </button>
      )}

      {/* Save footer */}
      {!isOnline && (
        <div style={s.footer}>
          <span style={s.footerLeft}>
            {newCount > 0
              ? <><strong style={{ color: "#374151" }}>{newCount}</strong> unsaved change{newCount !== 1 ? "s" : ""}</>
              : "All changes saved"
            }
          </span>
          <button
            style={{ ...s.saveBtn, ...(isSaveDisabled ? s.saveBtnDisabled : {}) }}
            disabled={isSaveDisabled}
            onClick={handleSave}
          >
            <Save size={13} />
            Save Vessels
          </button>
        </div>
      )}

      {/* Container section */}
      {!isOnline && (
        <>
          <hr style={s.divider} />
          <ContainerOffline
            containerData={containerData}
            setContainerData={setContainerData}
            oceanTrackingNumber={awbNumber}
            getListOfflineContainer={getListOfflineContainer}
            trackingData={trackingData}
          />
        </>
      )}

    </div>
  );
}
